import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import startCase from "lodash/startCase";

import { UseUserDetails } from "../../../hooks/auth";
import { UseCompanyWorkspaces } from "../../../hooks/company";
import { isKpiGamified } from "../../../constants/kpi";
import { GET_GROUP_KPIS, GET_ALL_USER_KPIS } from "../../../api/kpi";
import { buildCompanyScoresByDay } from "../../../hooks/kpi";
import { isRateLikeKpi } from "../../../helpers/scoreAggregate";
import { companyToday } from "../../../helpers/companyTime";
import { periodWindowLabel } from "../../../helpers/periodWindowLabel";
import { monthsForWindow, resolveWindow } from "./useWorkspaceData";

/* Dashboard-wide period presets. Scores are one month per call; each preset
   maps to the YYYY-MM set it needs (see monthsForWindow). */
export const PERIODS = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "week", label: "This week" },
  { id: "lastWeek", label: "Last week" },
  { id: "lastMonth", label: "Last month" },
  { id: "month", label: "This month" },
  { id: "quarter", label: "This quarter" },
  { id: "year", label: "This year" },
  { id: "custom", label: "Custom range" },
];

export const periodLabelFor = (id, custom = null) => {
  if (id === "quarter") {
    return periodWindowLabel("quarter");
  }
  if (id === "custom" && custom?.from && custom?.to) {
    return periodWindowLabel("custom", custom);
  }
  return (PERIODS.find((p) => p.id === id) || PERIODS.find((p) => p.id === "month")).label.toLowerCase();
};

/** Moment month starts covering the active board window. */
const monthsFor = (period, custom = null) => {
  const keys = monthsForWindow(period === "year" ? "ytd" : period, custom);
  return keys.map((k) => moment(`${k}-01`).startOf("month"));
};

/**
 * Loads the company's metrics and their real daily history, then exposes them
 * as chart-ready series for the dashboard builder.
 *
 * Metrics are deduped by name (the same metric can exist as several per-workspace
 * GroupKpis); a metric's series is the sum of its underlying kpiIds so a
 * dashboard reads company-wide. All numbers come from the same pipeline the
 * Company Metrics cards already use — no new server aggregation.
 *
 * `period` widens or narrows every series/summary at once (today / week /
 * month / year); month responses are cached so switching back is instant.
 */
export default function useDashboardMetrics() {
  const { companyId } = UseUserDetails();
  const rawWorkspaces = UseCompanyWorkspaces(true);
  const workspaces = useMemo(() => rawWorkspaces || [], [rawWorkspaces]);
  const [metrics, setMetrics] = useState([]);
  const [groupIds, setGroupIds] = useState([]);
  const [scoresByDay, setScoresByDay] = useState({});
  const [period, setPeriod] = useState("month");
  const [customRange, setCustomRange] = useState({ from: "", to: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // "<groupId>|<YYYY-MM>" -> that month's byDay fragment.
  const monthCache = useRef({});
  const customKey = `${customRange?.from || ""}~${customRange?.to || ""}`;

  const loadMetrics = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError("");
    try {
      const kpiRes = await GET_GROUP_KPIS(companyId);
      // Scorekeeper-only still hides a metric from leaderboards and player
      // columns, but NOT from this admin Analytics catalog: Company Metrics
      // and Workspace Metrics both list it, so the component builder must
      // offer it too (Hours Worked is Scorekeeper-only and vanished here).
      const activeKpis = (kpiRes.data || []).filter((k) => !k.disabled);

      // Group per-workspace KPIs into one company metric per name.
      // Keep which workspaces own each name so Create-a-component can filter
      // the picker to the workspaces the card is scoped to. A metric's own
      // groupId is its home workspace (or the company template placeholder);
      // linkedGroupIds are the workspaces it was added to afterward via
      // "Assign to workspace(s)" — both count as "this metric is on that
      // workspace", or an assigned-only metric never gets a workspaceIds
      // entry and silently can't be picked for any workspace.
      const byTitle = new Map();
      activeKpis.forEach((k) => {
        const title = startCase(k.name ?? k.model?.type ?? "Metric");
        const target = k.target ?? k.model?.defaultTarget ?? 0;
        const linkedIds = (k.linkedGroupIds || []).map((id) => String(id?._id || id)).filter(Boolean);
        const gids = [...new Set([k.groupId ? String(k.groupId) : null, ...linkedIds].filter(Boolean))];
        const sourceRaw = k.sourceName || k.source || k.parameters?.sourceName || "";
        let source = "";
        if (typeof sourceRaw === "string") source = sourceRaw.trim();
        else if (sourceRaw && typeof sourceRaw === "object") {
          source = String(sourceRaw.name || sourceRaw.label || sourceRaw.title || sourceRaw.code || "").trim();
        }
        const rate = isRateLikeKpi({ ...k, kind: k.kind ?? k.model?.kind });
        const existing = byTitle.get(title);
        if (existing) {
          existing.rate = existing.rate || rate;
          existing.ids.push(k._id);
          existing.target += target || 0;
          existing.gamified = existing.gamified || isKpiGamified(k);
          gids.forEach((gid) => { if (!existing.workspaceIds.includes(gid)) existing.workspaceIds.push(gid); });
          if (source && !existing.sourceName) existing.sourceName = source;
        } else {
          byTitle.set(title, {
            id: title,
            title,
            ids: [k._id],
            target: target || 0,
            granularity: k.chartGranularity || "day",
            kind: k.model?.kind,
            type: k.model?.type,
            gamified: isKpiGamified(k),
            rate,
            workspaceIds: gids,
            sourceName: source || undefined,
          });
        }
      });

      const companyMetrics = Array.from(byTitle.values());

      // "Technicians' Revenue per Hour" needs Technicians' OWN hours, not the
      // company-wide "Hours Worked" total above (other departments carry the
      // same title, and byTitle already merged all of them together). Add one
      // extra, workspace-locked entry for just that workspace's Hours Worked
      // KPI — everything else about it (fetch, series, summary) still goes
      // through the exact same pipeline as any other metric here.
      const techWs = workspaces.find((w) => /technician/i.test(w?.name || ""));
      const techHoursKpi = techWs
        ? activeKpis.find(
            (k) =>
              String(k.groupId) === String(techWs._id) &&
              (k.type === "hours_worked" || k?.model?.type === "hours_worked"),
          )
        : null;
      if (techHoursKpi) {
        companyMetrics.push({
          id: `__hours_worked__${techWs._id}`,
          title: `Hours Worked (${techWs.name})`,
          ids: [techHoursKpi._id],
          target: techHoursKpi.target ?? techHoursKpi.model?.defaultTarget ?? 0,
          granularity: techHoursKpi.chartGranularity || "day",
          kind: techHoursKpi.model?.kind,
          type: techHoursKpi.model?.type,
          gamified: isKpiGamified(techHoursKpi),
          workspaceIds: [String(techWs._id)],
          scoped: true,
        });
      }

      setMetrics(companyMetrics);
      // The scores endpoint only returns data when given a workspace id
      // (without one its filters match nothing) — remember which ones exist,
      // home workspace and every linked-on workspace alike.
      const allGroupIds = activeKpis.flatMap((k) => [
        k.groupId,
        ...(k.linkedGroupIds || []).map((id) => id?._id || id),
      ]);
      setGroupIds([...new Set(allGroupIds.filter(Boolean).map(String))]);
    } catch (err) {
      setError(err?.message || "Could not load metrics.");
      setLoading(false);
    }
  }, [companyId, workspaces]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  // Merge "User" scopes with any shared scores per response, mirroring useKpiData().
  const mergeUserScores = (res) => {
    const scores = res.data?.scores || [];
    const perUser = [];
    // Shared/company-wide scores (e.g. Group-owned rollups like Total
    // Revenue) - dedupe by owner in case the same score comes back more
    // than once, and add once via a single synthetic entry rather than
    // attaching to every user, which previously multiplied the total by
    // the workspace's user count.
    const sharedByOwner = new Map();
    scores.forEach((s) => {
      if (s.scope === "User") perUser.push({ ...s, days: [...(s.days || [])] });
      else {
        const key = s.ownerId ?? s.userId ?? s._id;
        if (!sharedByOwner.has(key)) sharedByOwner.set(key, s.days ?? []);
      }
    });
    const shared = [...sharedByOwner.values()].flat();
    if (shared.length) {
      perUser.push({ ownerId: "__shared__", days: shared });
    }
    return perUser;
  };

  // Fetch every (workspace × month) the current period needs, then merge the
  // per-month byDay maps into one. Cached fragments skip the network.
  useEffect(() => {
    if (!companyId || !groupIds.length) return undefined;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const months = monthsFor(period, period === "custom" ? customRange : null);
        const jobs = [];
        months.forEach((m) => {
          const monthKey = m.format("YYYY-MM");
          // Current month keeps the no-param call (server excludes archived
          // entries on that path — same numbers the Metrics page shows).
          const isCurrent = m.isSame(companyToday(), "month");
          groupIds.forEach((gid) => {
            const key = `${gid}|${monthKey}`;
            if (monthCache.current[key]) return;
            jobs.push(
              GET_ALL_USER_KPIS(companyId, gid, isCurrent ? null : m.format("YYYY-MM-DD"))
                .then((res) => {
                  monthCache.current[key] = buildCompanyScoresByDay(mergeUserScores(res), m);
                })
                .catch(() => {
                  monthCache.current[key] = {};
                })
            );
          });
        });
        await Promise.all(jobs);
        if (!alive) return;
        const merged = {};
        months.forEach((m) => {
          const monthKey = m.format("YYYY-MM");
          groupIds.forEach((gid) => {
            const frag = monthCache.current[`${gid}|${monthKey}`] || {};
            Object.entries(frag).forEach(([day, kpis]) => {
              if (!merged[day]) merged[day] = {};
              Object.entries(kpis).forEach(([kpiId, val]) => {
                if (kpiId === "total") return;
                const amt = val && typeof val.amt === "number" ? val.amt : Number(val) || 0;
                const pts = val && typeof val.pts === "number" ? val.pts : 0;
                if (!merged[day][kpiId]) merged[day][kpiId] = { amt: 0, pts: 0 };
                merged[day][kpiId].amt += amt;
                merged[day][kpiId].pts += pts;
              });
            });
          });
        });
        setScoresByDay(merged);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [companyId, groupIds, period, customKey]);

  const getMetric = useCallback(
    (id) => metrics.find((m) => m.id === id) || null,
    [metrics]
  );

  // Series for a metric = totals across its kpiIds, shaped by the period.
  const getSeries = useCallback(
    (id, granularity) => {
      const metric = metrics.find((m) => m.id === id);
      if (!metric) return [];
      const dayValue = (m) => {
        const kpis = scoresByDay[m.format("YYYY-MM-DD")];
        if (!kpis) return 0;
        return metric.ids.reduce((sum, kpiId) => {
          const v = kpis[kpiId];
          if (v && typeof v.amt === "number") return sum + v.amt;
          if (typeof v === "number") return sum + v;
          return sum;
        }, 0);
      };

      const { from, to } = resolveWindow(
        period === "year" ? "ytd" : period,
        period === "custom" ? customRange : null,
      );

      if (period === "today" || period === "yesterday") {
        return [{ timestamp: period === "today" ? "Today" : "Yesterday", value: dayValue(from) }];
      }
      if (period === "year") {
        const now = companyToday();
        return Array.from({ length: now.month() + 1 }, (_, i) => {
          const m = companyToday().startOf("year").add(i, "months");
          let total = 0;
          for (let d = 1; d <= m.daysInMonth(); d += 1) {
            total += dayValue(moment(m).date(d));
          }
          return { timestamp: m.format("MMM"), value: total };
        });
      }

      /* week / lastWeek / month / custom — daily points clipped to [from, to]. */
      const days = [];
      for (let d = from.clone().startOf("day"); !d.isAfter(to, "day"); d.add(1, "day")) {
        days.push({
          timestamp: period === "month" ? `${d.date()}` : d.format("MMM D"),
          value: dayValue(d),
        });
      }
      const gran = granularity || "day";
      if (gran !== "week" || period !== "month") return days;
      const weeks = [];
      days.forEach((pt, i) => {
        const w = Math.floor(i / 7);
        if (!weeks[w]) weeks[w] = { timestamp: `Wk ${w + 1}`, value: 0 };
        weeks[w].value += pt.value;
      });
      return weeks;
    },
    [metrics, scoresByDay, period, customKey]
  );

  // Headline number + a simple first-half vs second-half trend.
  const getSummary = useCallback(
    (id) => {
      const metric = metrics.find((m) => m.id === id);
      const label = periodLabelFor(period, customRange);
      if (!metric) return { total: 0, target: 0, deltaPct: null, periodLabel: label };
      const series = getSeries(id);
      const total = series.reduce((s, p) => s + (p.value || 0), 0);
      const mid = Math.floor(series.length / 2) || 1;
      /* Rates (Closed Rate, AVG Ticket, Rev Cred Per Hr, ...) are month
         snapshots written on the 1st — summing halves read the empty second
         half as a ▼100% drop. Average each half's readings instead, and show
         no trend when a half has none. */
      const fold = (pts) => {
        if (!metric.rate) return pts.reduce((s, p) => s + p.value, 0);
        const nz = pts.map((p) => Number(p.value) || 0).filter((v) => v > 0);
        return nz.length ? nz.reduce((a, b) => a + b, 0) / nz.length : null;
      };
      const first = fold(series.slice(0, mid));
      const second = fold(series.slice(mid));
      const deltaPct = series.length > 1 && first > 0 && second != null
        ? ((second - first) / first) * 100
        : null;
      return { total, target: metric.target || 0, deltaPct, periodLabel: label };
    },
    [metrics, getSeries, period, customKey]
  );

  return {
    companyId,
    metrics,
    loading,
    error,
    period,
    setPeriod,
    customRange,
    setCustomRange,
    periodLabel: periodLabelFor(period, customRange),
    reload: loadMetrics,
    getMetric,
    getSeries,
    getSummary,
  };
}

// Rank so the useful picks lead: metrics with real data first (gamified ones
// ahead within that, then biggest value), zeros sink to the bottom.
export const rankMetrics = (metrics, getSummary) => {
  const scored = (metrics || []).map((m) => ({
    m,
    total: getSummary(m.id).total || 0,
    gamified: m.gamified ? 1 : 0,
  }));
  scored.sort((a, b) => {
    const hasA = a.total > 0 ? 1 : 0;
    const hasB = b.total > 0 ? 1 : 0;
    if (hasB !== hasA) return hasB - hasA;
    if (b.gamified !== a.gamified) return b.gamified - a.gamified;
    return b.total - a.total;
  });
  return scored.map((s) => s.m);
};

// Lightweight display formatting shared across widgets.
export const isMoneyMetric = (metric) =>
  /revenue|sales|amount|cost|price|value|mrr|\$/i.test(metric?.title || "");

export const formatMetricValue = (metric, value) => {
  const n = Number(value) || 0;
  const rounded = Math.round(n * 100) / 100;
  const withCommas = rounded.toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });
  return isMoneyMetric(metric) ? `$${withCommas}` : withCommas;
};

// Compact axis ticks: 1.2k, 3.4M …
export const formatCompact = (value) => {
  const n = Number(value) || 0;
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return `${Math.round(n)}`;
};

/* ---- Combined widgets: one card calculated from two metrics ---- */
export const COMBINE_OPS = [
  { id: "add", sym: "+" },
  { id: "subtract", sym: "\u2212" },
  { id: "multiply", sym: "\u00d7" },
  { id: "divide", sym: "\u00f7" },
];

export const opSymbol = (id) => (COMBINE_OPS.find((o) => o.id === id) || COMBINE_OPS[3]).sym;

export const applyOp = (a, b, op) => {
  if (op === "add") return a + b;
  if (op === "subtract") return a - b;
  if (op === "multiply") return a * b;
  return b ? a / b : null; // divide-by-zero shows as a gap, never Infinity
};

/**
 * metric/series/summary for a widget carrying options.combine = { metric, op }.
 * Series combines point by point; the headline combines the period totals
 * (sum first, then operate — the true rate for ratios). Reads only data the
 * dashboard already loaded, so nothing new is fetched and no scores change.
 */
export const combineData = (api, widget) => {
  const cfg = widget?.options?.combine;
  if (!cfg || !api?.getMetric) return null;
  const a = api.getMetric(widget.metric);
  const b = api.getMetric(cfg.metric);
  if (!a || !b) return null;
  const op = cfg.op || "divide";
  const gran = widget.options?.granularity;
  const sa = api.getSeries(widget.metric, gran);
  const sb = api.getSeries(cfg.metric, gran);
  const series = sa.map((pt, i) => ({
    timestamp: pt.timestamp,
    value: applyOp(pt.value || 0, sb[i]?.value || 0, op),
  }));
  const sum = (s, from = 0, to = s.length) =>
    s.slice(from, to).reduce((x, pnt) => x + (pnt.value || 0), 0);
  const total = applyOp(sum(sa), sum(sb), op);
  const mid = Math.floor(sa.length / 2) || 1;
  const first = applyOp(sum(sa, 0, mid), sum(sb, 0, mid), op);
  const second = applyOp(sum(sa, mid), sum(sb, mid), op);
  const deltaPct =
    sa.length > 1 && first != null && second != null && first !== 0
      ? ((second - first) / first) * 100
      : null;
  const title = `${a.title} ${opSymbol(op)} ${b.title}`;
  return {
    metric: { id: `${widget.metric}~${op}~${cfg.metric}`, title, target: 0, gamified: false, combined: true },
    series,
    summary: { total: total ?? 0, target: 0, deltaPct, periodLabel: api.periodLabel || "" },
  };
};

/* ---- Average-per-period widgets: one metric divided by a day count, not
   another metric ---- */

/** Mon–Fri days between two moments, inclusive. */
export const businessDaysInRange = (from, to) => {
  if (!from?.isValid?.() || !to?.isValid?.()) return 0;
  let count = 0;
  const d = from.clone().startOf("day");
  const end = to.clone().startOf("day");
  while (d.isSameOrBefore(end, "day")) {
    const dow = d.day();
    if (dow > 0 && dow < 6) count += 1;
    d.add(1, "day");
  }
  return count;
};

/**
 * metric/series/summary for a widget carrying options.avgDivisor = "businessDays".
 * The series stays the metric's own real daily numbers (so the card still
 * shows what actually happened each day); only the headline total becomes
 * that period sum divided by the business-day count for the active period —
 * "Average Daily Revenue" reads the same 22 business days in a month whether
 * or not weekends happened to have any revenue on them.
 */
export const averageData = (api, widget) => {
  const divisor = widget?.options?.avgDivisor;
  if (!divisor || !api?.getMetric) return null;
  const metric = api.getMetric(widget.metric);
  if (!metric) return null;
  const gran = widget.options?.granularity;
  const series = api.getSeries(widget.metric, gran);

  const { from, to } = resolveWindow(
    api.period === "year" ? "ytd" : api.period,
    api.period === "custom" ? api.customRange : null,
  );
  const days = divisor === "businessDays" ? businessDaysInRange(from, to) : 0;

  const sum = (s) => s.reduce((x, pnt) => x + (pnt.value || 0), 0);
  const total = days > 0 ? sum(series) / days : null;
  const divisorLabel = divisor === "businessDays" ? "Average Daily" : "Average";
  const title = `${divisorLabel} ${metric.title}`;

  return {
    metric: { id: `${widget.metric}~avg~${divisor}`, title, target: 0, gamified: false, combined: true },
    series,
    summary: { total: total ?? 0, target: 0, deltaPct: null, periodLabel: api.periodLabel || "" },
  };
};

/** metric/series/summary for any widget, real, combined or averaged — the
    one place the board, the mobile stack and the edit preview should all
    read from so a saved Combine/Average widget renders the same everywhere
    it appears, not just in the editor. */
export const resolveWidgetData = (api, widget) => {
  if (widget?.options?.combine) {
    const cd = combineData(api, widget);
    if (cd) return cd;
  }
  if (widget?.options?.avgDivisor) {
    const ad = averageData(api, widget);
    if (ad) return ad;
  }
  return {
    metric: api?.getMetric ? api.getMetric(widget?.metric) : null,
    series: api?.getSeries ? api.getSeries(widget?.metric, widget?.options?.granularity) : [],
    summary: api?.getSummary ? api.getSummary(widget?.metric) : {},
  };
};
